import { inject, observer } from "mobx-react";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import LoadingOverlay from "../../../components/Loading/overlay";
import LoginAsUserStore from "../../../modules/Admin/LoginAsUser/store";
import { SSOLoginAsUserGridListDto } from "../../../modules/Admin/LoginAsUser/types";
import { SSOExternalUserGridListDto } from "../../../modules/Admin/ExternalUserManagement/types";
import { MessageResultState } from "../../../types/general-types";
import { upsertAccount } from "../../../utils/SsoSession";
import { REDESIGN_NS } from "../../i18n";
import { Modal, ModalFooter } from "../../ui/Modal";

/** Mevcut ekranla aynı minimum. */
const MIN_EXPLANATION_LENGTH = 10;

interface Props {
  LoginAsUserStore?: LoginAsUserStore;
  isOpen: boolean;
  onClose: () => void;
  user?: SSOLoginAsUserGridListDto;
  externalUser?: SSOExternalUserGridListDto;
  isExternal: boolean;
}

const ProxyLoginModal: FC<Props> = inject("LoginAsUserStore")(
  observer(
    ({
      LoginAsUserStore: store,
      isOpen,
      onClose,
      user,
      externalUser,
      isExternal,
    }: Props) => {
      const { t } = useTranslation(REDESIGN_NS);
      const { register, handleSubmit, errors } = useForm();
      const history = useHistory();

      const [explanation, setExplanation] = useState("");
      const [isSubmitting, setIsSubmitting] = useState(false);

      const submit = handleSubmit(async () => {
        setIsSubmitting(true);
        try {
          const response = isExternal
            ? await store?.loginAsExternalUser({
                userName: externalUser?.username || "",
                explanation,
              })
            : await store?.loginAsUser({
                personnelId: user?.personnelId || "",
                explanation,
              });

          if (response?.state === MessageResultState.SUCCESS) {
            upsertAccount(
              {
                sessionKey: response.result.sessionKey,
                userName: response.result.user?.username,
                email: response.result.user?.email,
                userRoles: response.result.user?.userRoles || [],
                isRoot: false,
              },
              true
            );
            history.push("portal-selection");
          }
        } finally {
          setIsSubmitting(false);
        }
      });

      return (
        <>
          <LoadingOverlay show={isSubmitting} />
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            width={560}
            title={t("Login As User")}
            footer={
              <ModalFooter
                onClose={onClose}
                onSubmit={submit}
                submitLabel={t("Login")}
                isBusy={isSubmitting}
              />
            }
          >
            <div className="sso-rd__form-grid sso-rd__form-grid--single">
              <div className="sso-rd__field">
                <label className="sso-rd__label" htmlFor="sso-rd-explanation">
                  {t("Explanation")}
                  <span className="sso-rd__req">*</span>
                </label>
                <input
                  id="sso-rd-explanation"
                  name="explanation"
                  type="text"
                  className={
                    "sso-rd__input" + (errors["explanation"] ? " is-invalid" : "")
                  }
                  placeholder={t("Explanation")}
                  value={explanation}
                  onChange={(event) => setExplanation(event.target.value)}
                  ref={register({
                    required: t("Please fill out this field.").toString(),
                    minLength: {
                      value: MIN_EXPLANATION_LENGTH,
                      message: t(
                        "The given value must be at least {{value}} characters.",
                        { value: MIN_EXPLANATION_LENGTH }
                      ),
                    },
                  })}
                />
                {errors["explanation"] && (
                  <div className="sso-rd__error">
                    {errors["explanation"].message}
                  </div>
                )}
              </div>
            </div>
          </Modal>
        </>
      );
    }
  )
) as FC<Props>;

export default ProxyLoginModal;
